import React from 'react'

import cx from 'classnames'

// MUI Imports
import {
  Card,
  CardContent,
  Divider,
  Typography,
  IconButton,
  Chip,
  Box
} from '@mui/material'
import {
  Reply,
  Navigation,
  AttachFile,
  Description
} from '@mui/icons-material'

// Component Imports
import { useTranslation } from 'react-i18next'

// Styles Imports
import styles from '../styles/mailbox.module.scss'
import McPageHelpers from '../../../../pages/mc-components/McPageHelpers'

const messageTypes = {
  OUTGOING: 'OUTGOING',
  INCOMING: 'INCOMING'
}

const CardHeaderAction = ({ data, isReplies, setRepliedMessage }) => {
  const userLocale = McPageHelpers.localeMap[localStorage.getItem('lang')] || 'en-US'

  return (
    <div className='flex flex-col'>
      <div className='flex items-center justify-end gap-4'>
        <Typography color='text.disabled'>
          {data?.sentTimeMillis &&
            new Intl.DateTimeFormat(userLocale, {
              year: 'numeric',
              month: 'short',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            }).format(new Date(data.sentTimeMillis))
          }
        </Typography>
        <div className='flex items-center gap-1'>
          <IconButton
          onClick={(e) => {
            setRepliedMessage(data)
          }}
          color='primary'
        >
          <Reply/>
          </IconButton>
        </div>
      </div>

      <Typography color='text.disabled'>
        {data?.subject}
      </Typography>
    </div>
  )
}

const MailCard = ({ data, isReplies, setRepliedMessage }) => {
  const { t } = useTranslation()

  const handleDownloadAttachment = (attachment) => {
    try {
      // Handle base64 encoded file bytes
      const base64Data = attachment.fileBytes
      const contentType = attachment.contentType || 'application/octet-stream'
      const fileName = attachment.fileName || 'attachment'

      // Convert base64 to blob
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: contentType })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading attachment:', error)
    }
  }

  return (
    <Card className='border'>
      <CardContent className='flex is-full gap-4 py-2'>
        <div className='flex items-center justify-between flex-wrap grow gap-x-4 gap-y-2'>
          <div className='flex items-center'>
             <Navigation
              color='primary'
              sx={{
                rotate: data?.type === messageTypes.INCOMING ? '210deg' : '30deg',
                marginRight: '10px'
              }}
            />
            <div className='flex flex-col'>
              <Typography variant='body2' color='text.secondary'>
                <strong>{t('mailTemplate.mailbox.list.fromHeader')}:</strong> {data?.from_name || data?.from_address}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>{t('mailTemplate.mailbox.list.to')}:</strong> {data?.to?.[0]?.name || data?.to?.[0]?.address}
              </Typography>
            </div>
          </div>
          <CardHeaderAction data={data} isReplies={isReplies} setRepliedMessage={setRepliedMessage}/>
        </div>
      </CardContent>
      <Divider />
      <CardContent>
        <div
          className={cx('text-textSecondary', styles.message)}
          dangerouslySetInnerHTML={{ __html: data?.bodyHtml }}
        />
        {data?.attachments?.length > 0 && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '2px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AttachFile sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant='subtitle2' color='text.secondary' sx={{ fontWeight: 600 }}>
                {t('mailTemplate.mailbox.attachments', { count: data.attachments.length })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {data.attachments.map((attachment, index) => {
                const fileName = attachment.fileName || attachment.name || `Attachment ${index + 1}`
                const fileSize = attachment.fileSize || attachment.size
                const fileSizeKB = fileSize ? Math.round(fileSize / 1024) : null
                const fileSizeDisplay = fileSizeKB
                  ? (fileSizeKB > 1024
                      ? `${(fileSizeKB / 1024).toFixed(2)} MB`
                      : `${fileSizeKB} KB`)
                  : null

                return (
                  <Chip
                    key={`${fileName}-${index}`}
                    icon={<Description />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {fileName}
                        </Typography>
                        {fileSizeDisplay && (
                          <Typography variant='caption' color='text.secondary'>
                            ({fileSizeDisplay})
                          </Typography>
                        )}
                      </Box>
                    }
                    variant='outlined'
                    onClick={() => handleDownloadAttachment(attachment)}
                    sx={{
                      py: 2,
                      px: 1,
                      height: 'auto',
                      cursor: 'pointer',
                      '& .MuiChip-label': {
                        display: 'block',
                        whiteSpace: 'normal'
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                )
              })}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default MailCard
